import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  loading?: boolean;
  children: ReactNode;
}

// Brand-dark control set: the primary CTA is the vivid brand orange on
// the deep-purple surface; secondary is the elevated purple with a
// light hairline; ghost is quiet cream text.
const VARIANT_CLASSES: Record<Variant, string> = {
  primary:
    'bg-brand-orange text-white hover:bg-primary-600 focus-visible:ring-primary-500 ' +
    'shadow-md shadow-brand-orange/20 disabled:bg-brand-orange/60',
  secondary:
    'bg-dark-surface text-brand-cream border border-white/10 hover:border-white/25 ' +
    'hover:bg-dark-border/40 focus-visible:ring-primary-500',
  ghost:
    'bg-transparent text-dark-secondary hover:text-brand-cream focus-visible:ring-primary-500',
};

export default function Button({
  variant = 'primary',
  loading = false,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  const variantClass = VARIANT_CLASSES[variant];
  const composedClass =
    'inline-flex min-h-touch items-center justify-center gap-2 rounded-md px-5 py-2.5 ' +
    'text-sm font-semibold transition focus:outline-none focus-visible:ring-2 ' +
    'focus-visible:ring-offset-2 focus-visible:ring-offset-brand-purple ' +
    'disabled:cursor-not-allowed ' +
    variantClass +
    (className ? ` ${className}` : '');
  return (
    <button
      {...rest}
      className={composedClass}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {loading ? (
        <span
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
      ) : null}
      {children}
    </button>
  );
}
